/**
 * Now / Completeness component.
 *
 * "Of the KPI instances this project is supposed to be reporting, how many
 * are currently caught up (have a recent-enough latest entry)?"
 *
 * Score = (instances_caught_up / total_instances) * 100
 *
 * DEV-851 (per-instance refactor): Granularity moved from per-category to
 * per-instance. The previous category-level rule rounded up — a Schedule
 * category with 5 instances where only 1 reported in the current period
 * still counted as "Schedule reported." The missing 4 instances were
 * invisible to the headline score.
 *
 * Now matches the DataReliabilityTab matrix's row-level status: each
 * configured instance is its own line item, and the score is the percentage
 * with a recent-enough `latest_reporting_date` (within cadence + grace of
 * as_of_date — the same gap-based rule the matrix uses).
 *
 * Lifecycle: requires ExpectedInstanceInput[]. App.tsx populates it from
 * kpiConfigStore (instance configs) + the per-instance stores (latest
 * reporting timestamps).
 *
 * See: DEV-846 epic, DEV-847, DEV-851 (PR2).
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

/**
 * Reporting period boundaries. The "current period" is the most recent
 * full period whose end date is on or before as_of_date. We measure
 * completeness for THAT period — the in-flight period (whose end date
 * hasn't arrived yet) is intentionally not measured here, since data for
 * it isn't yet expected.
 */
function periodBoundsFor(
  cadence: 'daily' | 'weekly' | 'monthly' | 'quarterly',
  asOfDate: string,
): { start: string; end: string } {
  const d = new Date(asOfDate);
  if (!Number.isFinite(d.getTime())) {
    // Fallback: treat as_of_date as today.
    return { start: asOfDate, end: asOfDate };
  }

  if (cadence === 'daily') {
    return { start: asOfDate, end: asOfDate };
  }

  if (cadence === 'weekly') {
    // Period ending = most recent Sunday on or before asOfDate.
    const day = d.getDay(); // 0 = Sunday
    const end = new Date(d);
    end.setDate(end.getDate() - day);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }

  if (cadence === 'monthly') {
    // Period = previous full month if as_of is past the 1st; else last month.
    // We use "most recent month-end on or before as_of_date" as the period end.
    const end = new Date(d.getFullYear(), d.getMonth(), 0); // last day of previous month
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }

  // quarterly
  const month = d.getMonth();
  // Most recent quarter-end on or before as_of_date.
  // Quarters end at months 2 (Mar), 5 (Jun), 8 (Sep), 11 (Dec).
  const quarterEndMonth = month >= 9 ? 8 : month >= 6 ? 5 : month >= 3 ? 2 : 11;
  const yearForEnd = month >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  const end = new Date(yearForEnd, quarterEndMonth + 1, 0);
  const start = new Date(yearForEnd, quarterEndMonth - 2, 1);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/* ------------------------------------------------------------------------- */
/*  Public                                                                   */
/* ------------------------------------------------------------------------- */

/** Days between two ISO dates (positive when b is after a). */
function daysBetween(a: string, b: string): number {
  const ma = Date.parse(a);
  const mb = Date.parse(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

/** Map cadence string → expected days between consecutive reports. */
function cadenceToDays(cadence: string): number {
  switch (cadence) {
    case 'daily':     return 1;
    case 'weekly':    return 7;
    case 'quarterly': return 91;
    case 'monthly':
    default:          return 30;
  }
}

export function computeCompleteness(
  inputs: DataReliabilityInputs,
): ComponentResult {
  const expected = inputs.expected_instances ?? [];

  // ─────────────────────────────────────────────────────────────────────
  // DEV-851 per-instance path: when the caller supplied expected_instances,
  // use the matrix-aligned rule. This is the production path.
  //
  // Two modes, picked per-instance:
  //   (A) Fractional coverage  — when expected_periods + missing_periods
  //       are populated (post-DEV-651, fractional-completeness work),
  //       the row contributes 1 − (missing / expected). Headline
  //       completeness becomes a literal mean of per-row coverage and
  //       reconciles against the visible matrix.
  //   (B) Legacy recency       — when fractional fields are absent, the
  //       row scores 1 (caught up) if its latest submission lies within
  //       cadence + grace of as_of_date, else 0. Same rule as the old
  //       binary classifier, kept for callers (tests, ActionCenter) that
  //       haven't migrated to passing allDatesIndices yet.
  // ─────────────────────────────────────────────────────────────────────
  if (expected.length > 0) {
    const defaultGrace = Math.max(0, inputs.grace_days);
    let scoreSum = 0;
    let scored = 0;
    const missingLabels: string[] = [];

    for (const inst of expected) {
      const grace = Number.isFinite(inst.grace_days)
        ? Math.max(0, inst.grace_days as number)
        : defaultGrace;
      const cadenceDays = cadenceToDays(inst.cadence);

      const hasFractional =
        Number.isFinite(inst.expected_periods) &&
        Number.isFinite(inst.missing_periods) &&
        (inst.expected_periods as number) > 0;

      if (hasFractional) {
        const expectedP = inst.expected_periods as number;
        const missingP = Math.max(0, Math.min(expectedP, inst.missing_periods as number));
        const ratio = 1 - missingP / expectedP;
        scoreSum += ratio;
        scored += 1;
        if (missingP > 0) {
          missingLabels.push(`${inst.category}/${inst.instance_id} (${missingP}/${expectedP} missing)`);
        }
        continue;
      }

      // Fall back to legacy recency check when fractional data is absent.
      // Skip rows that are not-yet-due (expected_periods === 0); they don't
      // contribute either positively or negatively, mirroring the matrix.
      if (Number.isFinite(inst.expected_periods) && (inst.expected_periods as number) === 0) {
        continue;
      }

      if (!inst.latest_reporting_date) {
        scoreSum += 0;
        scored += 1;
        missingLabels.push(`${inst.category}/${inst.instance_id}`);
        continue;
      }
      const daysSince = daysBetween(inst.latest_reporting_date, inputs.as_of_date);
      if (daysSince <= cadenceDays + grace) {
        scoreSum += 1;
      } else {
        missingLabels.push(`${inst.category}/${inst.instance_id}`);
      }
      scored += 1;
    }

    if (scored === 0) {
      return {
        key: 'completeness',
        score: null,
        applicable: false,
        reason: 'No KPI instances are due to report yet.',
        explain: 'Project too new for any configured KPI to have entered its first reporting period.',
      };
    }

    const score = Math.round((scoreSum / scored) * 100);
    const allCaughtUp = missingLabels.length === 0;
    const explain = allCaughtUp
      ? `All ${scored} configured KPI${scored === 1 ? '' : 's'} are caught up.`
      : `${scored - missingLabels.length} of ${scored} KPI${scored === 1 ? ' is' : 's are'} fully caught up; ${missingLabels.length} stale or missing.`;

    return {
      key: 'completeness',
      score,
      applicable: true,
      explain,
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // Legacy per-category path: kept for existing tests + portfolio-level
  // callers that don't supply expected_instances. Pre-DEV-851 behavior.
  // ─────────────────────────────────────────────────────────────────────
  const cadenceMap = inputs.reporting_cadence;
  const categories: Array<'schedule' | 'finance' | 'mission'> = [];
  if (cadenceMap.schedule) categories.push('schedule');
  if (cadenceMap.finance) categories.push('finance');
  if (cadenceMap.mission) categories.push('mission');

  if (categories.length === 0) {
    return {
      key: 'completeness',
      score: null,
      applicable: false,
      reason: 'No reporting cadence configured for any category',
      explain: 'No categories have a reporting cadence configured.',
    };
  }

  let categoriesReported = 0;
  const perCategory: Record<string, boolean> = {};

  for (const cat of categories) {
    const cad = cadenceMap[cat]!;
    const bounds = periodBoundsFor(cad, inputs.as_of_date);
    const reported = inputs.progress_entries.some(
      (e) =>
        e.category === cat &&
        e.reporting_date >= bounds.start &&
        e.reporting_date <= bounds.end,
    );
    perCategory[cat] = reported;
    if (reported) categoriesReported += 1;
  }

  const startDate = inputs.project_metadata.start_date;
  if (startDate) {
    const allBoundsBeforeStart = categories.every((cat) => {
      const cad = cadenceMap[cat]!;
      const bounds = periodBoundsFor(cad, inputs.as_of_date);
      return bounds.end < startDate;
    });
    if (allBoundsBeforeStart) {
      return {
        key: 'completeness',
        score: null,
        applicable: false,
        reason: 'No reporting period has completed since project start',
        explain: 'Project too new to measure completeness.',
      };
    }
  }

  const score = Math.round((categoriesReported / categories.length) * 100);
  const explain =
    categoriesReported === categories.length
      ? `All ${categories.length} expected categor${categories.length === 1 ? 'y' : 'ies'} reported this period.`
      : `${categoriesReported} of ${categories.length} categor${categories.length === 1 ? 'y' : 'ies'} reported this period (missing: ${categories.filter((c) => !perCategory[c]).join(', ')}).`;

  return {
    key: 'completeness',
    score,
    applicable: true,
    explain,
  };
}
